import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { AdBanner } from '../components/AdUnit';
import { fetchAdSenseConfig, DEFAULT_ADSENSE_CONFIG, type AdSenseConfig } from '../lib/adsense-config';
import {
  fetchRepairCases,
  searchRepairCases,
  fetchRepairReferences,
  searchRepairReferences,
  fetchTaxonomy,
  familiesForManufacturer,
  modelsForFamily,
  revisionsForFamily,
  REF_MEASUREMENT_TYPES,
  measurementTypeLabel,
  type KbTaxonomy,
  type KbManufacturer,
} from '../lib/repair-kb-client';
import type { RepairCase, RepairReference } from '../lib/repair-kb-client';
import { Search, ChevronRight, Wrench, FileText, Cpu, Loader, X } from '../lib/icons';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string | null) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function resultColor(result: string) {
  switch (result) {
    case 'repaired':           return 'text-green-400 bg-green-900/30 border-green-800';
    case 'partially-repaired': return 'text-yellow-400 bg-yellow-900/30 border-yellow-800';
    case 'no-fix':             return 'text-red-400 bg-red-900/30 border-red-800';
    default:                   return 'text-gray-400 bg-gray-800 border-gray-700';
  }
}

function resultLabel(result: string) {
  const map: Record<string, string> = {
    'repaired': 'Repaired', 'partially-repaired': 'Partial Fix',
    'no-fix': 'No Fix', 'board-donor': 'Board Donor', 'further-diagnosis': 'Further Diagnosis',
  };
  return map[result] ?? result;
}

// Build URL for a repair case given its taxonomy IDs
function caseUrl(c: RepairCase, taxonomy: KbTaxonomy): string {
  const family = taxonomy.device_families.find(f => f.id === c.device_family_id);
  if (!family) return `/repair-database/${c.slug}`;
  const board = c.board_revision_id
    ? taxonomy.board_revisions.find(b => b.id === c.board_revision_id)
    : null;
  if (board) {
    const boardSegment = board.name.toLowerCase().replace(/\s+/g, '-');
    return `/repair-database/${family.slug}/${boardSegment}/${c.slug}`;
  }
  return `/repair-database/${family.slug}/${c.slug}`;
}

// ── Search result cards ───────────────────────────────────────────────────────

function CaseResultCard({ c, taxonomy }: { c: RepairCase; taxonomy: KbTaxonomy }) {
  const family = taxonomy.device_families.find(f => f.id === c.device_family_id);
  const board  = c.board_revision_id ? taxonomy.board_revisions.find(b => b.id === c.board_revision_id) : null;
  const mfr    = family ? taxonomy.manufacturers.find(m => m.id === family.manufacturer_id) : null;
  const thumb  = c.repair_case_media?.[0]?.public_url ?? c.featured_image_url ?? '';
  const [imgErr, setImgErr] = useState(false);

  return (
    <Link to={caseUrl(c, taxonomy)}
      className="group flex gap-4 bg-gray-900 border border-gray-800 hover:border-gray-600 rounded-xl p-4 transition-all">
      {thumb && !imgErr ? (
        <img src={thumb} alt="" onError={() => setImgErr(true)}
          className="w-20 h-20 object-cover rounded-lg flex-shrink-0" />
      ) : (
        <div className="w-20 h-20 bg-gray-800 rounded-lg flex-shrink-0 flex items-center justify-center">
          <Cpu className="h-6 w-6 text-gray-700" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FF1744]/20 border border-[#FF1744]/40 text-[#FF1744]">Repair</span>
          {mfr && <span className="text-xs text-gray-500">{mfr.name}</span>}
          {family && <span className="text-xs text-gray-400">{family.name}</span>}
          {board && <span className="text-xs font-mono bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded">{board.name}</span>}
        </div>
        <p className="text-sm font-semibold text-[#E0E0E0] group-hover:text-white truncate">
          {c.title || (family ? `${family.name} Repair` : 'Repair Case')}
        </p>
        <div className="flex items-center gap-3 mt-1">
          {c.result && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${resultColor(c.result)}`}>
              {resultLabel(c.result)}
            </span>
          )}
          {c.published_at && (
            <span className="text-[10px] text-gray-600">{formatDate(c.published_at)}</span>
          )}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-gray-600 group-hover:text-[#FF1744] self-center flex-shrink-0 transition-colors" />
    </Link>
  );
}

function RefResultCard({ r, taxonomy }: { r: RepairReference; taxonomy: KbTaxonomy }) {
  const family = taxonomy.device_families.find(f => f.id === r.device_family_id);
  const board  = r.board_revision_id ? taxonomy.board_revisions.find(b => b.id === r.board_revision_id) : null;
  const mfr    = family ? taxonomy.manufacturers.find(m => m.id === family.manufacturer_id) : null;

  return (
    <Link to={`/repair-database/ref/${r.slug || r.id}`}
      className="group flex gap-4 bg-gray-900 border border-gray-800 hover:border-[#00BFFF]/40 rounded-xl p-4 transition-all">
      <div className="w-20 h-20 bg-gray-800 rounded-lg flex-shrink-0 flex items-center justify-center">
        {r.repair_reference_media?.[0]?.public_url ? (
          <img src={r.repair_reference_media[0].public_url} alt=""
            className="w-full h-full object-cover rounded-lg" />
        ) : (
          <FileText className="h-6 w-6 text-gray-700" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#00BFFF]/20 border border-[#00BFFF]/40 text-[#00BFFF]">Reference</span>
          {mfr && <span className="text-xs text-gray-500">{mfr.name}</span>}
          {family && <span className="text-xs text-gray-400">{family.name}</span>}
          {board && <span className="text-xs font-mono bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded">{board.name}</span>}
          {r.measurement_type && (() => {
            const t = REF_MEASUREMENT_TYPES.find(x => x.value === r.measurement_type);
            return t ? <span className="text-[10px] text-[#00BFFF]/70 bg-[#00BFFF]/10 border border-[#00BFFF]/20 px-2 py-0.5 rounded-full">{t.shortLabel}</span> : null;
          })()}
        </div>
        <p className="text-sm font-semibold text-[#E0E0E0] group-hover:text-white truncate">{r.title}</p>
        {r.published_at && (
          <span className="text-[10px] text-gray-600 mt-1 block">{formatDate(r.published_at)}</span>
        )}
      </div>
      <ChevronRight className="h-4 w-4 text-gray-600 group-hover:text-[#00BFFF] self-center flex-shrink-0 transition-colors" />
    </Link>
  );
}

// ── Manufacturer browse card ───────────────────────────────────────────────────

function ManufacturerCard({
  mfr, taxonomy, caseCount, refCount,
}: {
  mfr: KbManufacturer;
  taxonomy: KbTaxonomy;
  caseCount: number;
  refCount: number;
}) {
  const families = familiesForManufacturer(taxonomy, mfr.id);
  const total = caseCount + refCount;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-white">{mfr.name}</h2>
          {total > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">
              {caseCount > 0 && `${caseCount} repair${caseCount !== 1 ? 's' : ''}`}
              {caseCount > 0 && refCount > 0 && ' · '}
              {refCount > 0 && `${refCount} reference${refCount !== 1 ? 's' : ''}`}
            </p>
          )}
        </div>
      </div>
      <div className="space-y-1">
        {families.map(f => (
          <Link key={f.id} to={`/repair-database/${f.slug}`}
            className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors group">
            <span className="text-sm text-[#E0E0E0] group-hover:text-white">{f.name}</span>
            <ChevronRight className="h-3.5 w-3.5 text-gray-600 group-hover:text-[#FF1744] transition-colors" />
          </Link>
        ))}
        {families.length === 0 && (
          <p className="text-xs text-gray-700 px-3 py-2">No devices yet</p>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function RepairDatabasePage() {
  const navigate = useNavigate();
  const [query,    setQuery]    = useState('');
  const [results,  setResults]  = useState<{ cases: RepairCase[]; refs: RepairReference[] } | null>(null);
  const [searching, setSearching] = useState(false);
  const [taxonomy, setTaxonomy] = useState<KbTaxonomy>({ manufacturers: [], device_families: [], models: [], board_revisions: [] });
  const [allCases,  setAllCases]  = useState<RepairCase[]>([]);
  const [allRefs,   setAllRefs]   = useState<RepairReference[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [adConfig,  setAdConfig]  = useState<AdSenseConfig>(DEFAULT_ADSENSE_CONFIG);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reference browse filters (cascade)
  const [refMfr,   setRefMfr]   = useState('');
  const [refFamily, setRefFamily] = useState('');
  const [refModel,  setRefModel]  = useState('');
  const [refMtype,  setRefMtype]  = useState('');

  useEffect(() => {
    fetchAdSenseConfig().then(setAdConfig).catch(() => {});
    Promise.all([fetchTaxonomy(), fetchRepairCases(false), fetchRepairReferences(false)])
      .then(([tax, cases, refs]) => { setTaxonomy(tax); setAllCases(cases); setAllRefs(refs); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const doSearch = useCallback(async (q: string) => {
    if (!q.trim()) { setResults(null); return; }
    setSearching(true);
    try {
      const [cases, refs] = await Promise.all([
        searchRepairCases(q),
        searchRepairReferences(q),
      ]);
      setResults({ cases, refs });
    } catch { setResults({ cases: [], refs: [] }); }
    finally   { setSearching(false); }
  }, []);

  const handleQuery = (q: string) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults(null); return; }
    debounceRef.current = setTimeout(() => doSearch(q), 300);
  };

  const handleClear = () => { setQuery(''); setResults(null); };

  // Counts per manufacturer (from all cases + refs)
  const casesPerMfr = (mfrId: string) => {
    const familyIds = new Set(taxonomy.device_families.filter(f => f.manufacturer_id === mfrId).map(f => f.id));
    return allCases.filter(c => c.device_family_id && familyIds.has(c.device_family_id)).length;
  };
  const refsPerMfr = (mfrId: string) => {
    const familyIds = new Set(taxonomy.device_families.filter(f => f.manufacturer_id === mfrId).map(f => f.id));
    return allRefs.filter(r => r.device_family_id && familyIds.has(r.device_family_id)).length;
  };

  // Only show manufacturers that have at least one device family
  const activeManufacturers = taxonomy.manufacturers
    .filter(m => taxonomy.device_families.some(f => f.manufacturer_id === m.id))
    .sort((a, b) => a.sort_order - b.sort_order);

  const hasSearchResults = results && (results.cases.length + results.refs.length) > 0;
  const searchEmpty = results && !hasSearchResults;

  // Cascade options for reference browse
  const refFamilies  = refMfr    ? familiesForManufacturer(taxonomy, refMfr)    : [];
  const refModels    = refFamily ? modelsForFamily(taxonomy, refFamily)          : [];
  const filteredRefs = allRefs.filter(r => {
    if (refFamily && r.device_family_id !== refFamily) return false;
    if (refModel  && r.model_id          !== refModel)  return false;
    if (refMtype  && r.measurement_type  !== refMtype)  return false;
    if (!refFamily && refMfr) {
      const familyIds = new Set(taxonomy.device_families.filter(f => f.manufacturer_id === refMfr).map(f => f.id));
      if (r.device_family_id && !familyIds.has(r.device_family_id)) return false;
    }
    return true;
  });
  const showRefBrowse = !!(refMfr || refFamily || refModel || refMtype);
  const refTypesPresent = [...new Set(allRefs.map(r => r.measurement_type).filter(Boolean))];

  const jsonLD = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Repair Knowledge Base — GameFix Solutions',
    description: 'Board-level repair documentation and known-good references for game consoles and handheld PCs.',
    url: 'https://gamefixsolutions.com/repair-database',
  };

  return (
    <div className="bg-[#0D0D0D] min-h-screen text-[#E0E0E0]">
      <SEOHead
        title="Console Repair Knowledge Base | GameFix Solutions"
        description="Board-level repair cases and known-good measurement references for PS5, Xbox Series X, Legion Go and more."
        canonical="https://gamefixsolutions.com/repair-database"
        jsonLD={jsonLD}
      />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="border-b border-gray-800/60 bg-gradient-to-b from-gray-900/40 to-transparent">
        <div className="max-w-5xl mx-auto px-6 py-12 md:py-16">
          <div className="flex items-center gap-3 mb-4">
            <Wrench className="h-6 w-6 text-[#FF1744]" />
            <p className="text-sm font-semibold text-[#FF1744] tracking-widest uppercase">Repair Knowledge Base</p>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3 leading-tight">
            Board-Level Repair Documentation
          </h1>
          <p className="text-gray-400 text-base max-w-2xl mb-8">
            Documented repairs and reference measurements from our technicians. Search by device, board revision, symptom, error code, or component.
          </p>

          {/* Search */}
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={e => handleQuery(e.target.value)}
              placeholder="Search: EDM-030, no power, F3502, HDMI, Legion Go…"
              className="w-full bg-gray-900 border border-gray-700 rounded-xl pl-12 pr-10 py-4 text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60 text-base shadow-lg"
            />
            {query && (
              <button onClick={handleClear} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">

        {/* ── Search results ────────────────────────────────────────────── */}
        {query && (
          <div className="mb-10">
            {searching && (
              <div className="flex items-center gap-3 text-gray-500 py-8">
                <Loader className="h-4 w-4 animate-spin" />
                <span className="text-sm">Searching…</span>
              </div>
            )}
            {!searching && searchEmpty && (
              <p className="text-gray-500 text-sm py-8">No results for &ldquo;{query}&rdquo;.</p>
            )}
            {!searching && hasSearchResults && (
              <>
                <p className="text-xs text-gray-600 mb-4">
                  {results!.cases.length + results!.refs.length} result{results!.cases.length + results!.refs.length !== 1 ? 's' : ''} for &ldquo;{query}&rdquo;
                </p>
                <div className="space-y-3">
                  {results!.cases.map(c => <CaseResultCard key={c.id} c={c} taxonomy={taxonomy} />)}
                  {results!.refs.map(r  => <RefResultCard  key={r.id} r={r}  taxonomy={taxonomy} />)}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Browse by Manufacturer ────────────────────────────────────── */}
        {!query && (
          <>
            {adConfig.enabled && (
              <AdBanner
                publisherId={adConfig.publisher_id}
                slotId={adConfig.placements.banner_slot_id}
                enabled={adConfig.placements.banner_enabled}
              />
            )}
            <h2 className="text-xl font-bold text-white mb-6">Browse by Manufacturer</h2>

            {loading && (
              <div className="flex items-center gap-3 text-gray-500 py-12">
                <Loader className="h-5 w-5 animate-spin" />
                <span>Loading…</span>
              </div>
            )}

            {!loading && activeManufacturers.length === 0 && (
              <div className="text-center py-16 text-gray-600">
                <Cpu className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No manufacturers configured yet.</p>
              </div>
            )}

            {!loading && activeManufacturers.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {activeManufacturers.map(m => (
                  <ManufacturerCard
                    key={m.id}
                    mfr={m}
                    taxonomy={taxonomy}
                    caseCount={casesPerMfr(m.id)}
                    refCount={refsPerMfr(m.id)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Browse Known-Good References ─────────────────────────────────── */}
      {!query && !loading && allRefs.length > 0 && (
        <div className="mt-14 mb-4">
          <h2 className="text-xl font-bold text-white mb-2">Browse Known-Good References</h2>
          <p className="text-sm text-gray-500 mb-6">Filter by device, model, or measurement type to find board-level reference values.</p>

          {/* Cascading filters */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <select
              className="bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#FF1744]/60"
              value={refMfr}
              onChange={e => { setRefMfr(e.target.value); setRefFamily(''); setRefModel(''); }}
            >
              <option value="">All manufacturers</option>
              {taxonomy.manufacturers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <select
              className="bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#FF1744]/60"
              value={refFamily}
              onChange={e => { setRefFamily(e.target.value); setRefModel(''); }}
              disabled={!refMfr}
            >
              <option value="">{refMfr ? 'All devices' : '— pick manufacturer —'}</option>
              {refFamilies.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <select
              className="bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#FF1744]/60"
              value={refModel}
              onChange={e => setRefModel(e.target.value)}
              disabled={!refFamily}
            >
              <option value="">{refFamily ? 'All models' : '— pick device —'}</option>
              {refModels.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <select
              className="bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#FF1744]/60"
              value={refMtype}
              onChange={e => setRefMtype(e.target.value)}
            >
              <option value="">All types</option>
              {refTypesPresent.map(t => (
                <option key={t} value={t}>{measurementTypeLabel(t)}</option>
              ))}
            </select>
          </div>

          {/* Results */}
          {showRefBrowse && (
            <>
              {filteredRefs.length === 0 ? (
                <p className="text-sm text-gray-600 py-6 text-center">No references match these filters.</p>
              ) : (
                <div className="space-y-3">
                  {filteredRefs.map(r => <RefResultCard key={r.id} r={r} taxonomy={taxonomy} />)}
                </div>
              )}
            </>
          )}

          {!showRefBrowse && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {allRefs.slice(0, 6).map(r => <RefResultCard key={r.id} r={r} taxonomy={taxonomy} />)}
              {allRefs.length > 6 && (
                <p className="text-xs text-gray-600 col-span-full text-center py-2">
                  {allRefs.length - 6} more — use filters above to browse
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <div className="border-t border-gray-800 mt-12">
        <div className="max-w-5xl mx-auto px-6 py-12 text-center">
          <h2 className="text-xl font-bold text-white mb-2">Need Your Console Repaired?</h2>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Our technicians handle board-level repairs at both our San Diego and Temecula locations, and we accept mail-in repairs nationwide.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/book-repair"
              className="inline-flex items-center justify-center gap-2 bg-[#FF1744] hover:bg-[#D50000] text-white font-bold px-6 py-3 rounded-xl transition-colors">
              Schedule a Repair <ChevronRight className="h-4 w-4" />
            </Link>
            <Link to="/mail-in-repairs"
              className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] font-semibold px-6 py-3 rounded-xl border border-gray-700 transition-colors">
              Mail In Your Console
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
